import type { CategoryFormState } from '@openx/types/targeting/category';
import { DomainTargeting, type UrlTargeting } from '@openx/types/targeting/domains';
import { usePrevious } from '@openx/utils/hooks/usePrevious';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext';
import { InlineInfo } from '../InlineInfo/InlineInfo';
import { checkIsInheritingFromAccount } from './utils/checkIsInheritingFromAccount';

type ResetToDefaultProps = {
  accountUid?: string;
  initState: CategoryFormState | UrlTargeting;
};

export const useResetToAccountDefault = ({ accountUid, initState }: ResetToDefaultProps) => {
  const path = window.location.pathname;
  const isTrafficSetPage = path.includes('/trafficsets');
  const isEditPage = path.includes('/edit');
  const isDetailsPage = path.includes('/details');
  const previousAccount = usePrevious(null);

  // default value added for OA, because it doesn't use this hook
  const { useAccountFetch = () => ({ account: null, isAccountLoading: false }) } = useTargetingContext();

  // hook is only used by Traffic Sets, to use it in other components you need to change the condition below
  const { account, isAccountLoading } = useAccountFetch(isTrafficSetPage ? accountUid : '') || {
    account: null,
    isAccountLoading: false,
  };

  const { t } = useTranslation();

  const shouldDisplayResetButton = !isAccountLoading && !!account;

  const isCategoryType = 'val' in initState;

  const hasConfiguredParamsInSection = useMemo(() => {
    if (isCategoryType) {
      const values = Array.from(initState.val ?? []);
      return values.some(v => String(v).trim().length > 0);
    }

    const urls = initState.urls ?? [];
    const hasNonEmptyUrl = urls.some(url => String(url).trim().length > 0);

    const inventoryListId = initState.inventorylist_id;
    const hasInventoryList = Boolean(String(inventoryListId ?? '').trim().length > 0);

    return hasNonEmptyUrl || hasInventoryList;
  }, [initState, isCategoryType]);

  const shouldDisplayInfoComponent = isTrafficSetPage && !!accountUid && hasConfiguredParamsInSection;

  const shouldTriggerUpdate = useMemo(
    () => account !== previousAccount && !isEditPage && !isDetailsPage,
    [account, previousAccount, isEditPage, isDetailsPage]
  );

  const blockedCategories = useMemo(
    () => (account?.exchange?.blocked_adcategories ? Object.keys(account.exchange.blocked_adcategories) : []),
    [account]
  );

  const blockedDomains = useMemo(
    () => (account?.exchange?.domains ? Object.keys(account.exchange.domains) : []),
    [account]
  );

  const accountDomainTargetingOption = account?.exchange?.domain_targeting_option ?? DomainTargeting.ALL_VARIATIONS;

  const isInheritingFromAccount = useMemo(
    () =>
      checkIsInheritingFromAccount({
        blockedCategories,
        blockedDomains,
        accountDomainTargetingOption,
        initState,
      }),
    [accountDomainTargetingOption, blockedCategories, blockedDomains, initState]
  );

  const inheritanceInfo = isInheritingFromAccount ? t('Inheriting') : t('Overriding');
  const objectTypeLabel = isCategoryType ? t('Category') : t('Domains');

  const infoText = (
    <>
      {t('This Line Item is')} <strong>{inheritanceInfo}</strong>{' '}
      {t("the Account's {objectTypeLabel} Settings", { objectTypeLabel })}
    </>
  );

  const inheritanceInfoComponent = shouldDisplayInfoComponent ? <InlineInfo infoText={infoText} /> : null;

  return {
    accountDomainTargetingOption,
    blockedCategories,
    blockedDomains,
    inheritanceInfoComponent,
    isAccountLoading,
    isInheritingFromAccount,
    shouldDisplayResetButton,
    shouldTriggerUpdate,
  };
};
